import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {MediaMatcher} from '@angular/cdk/layout';
import {MatPaginator} from '@angular/material/paginator';
import {MatSidenav} from '@angular/material/sidenav';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LoggedInCallback} from '../../../service-auth/cognito.service';
import {RoleChangeListener, StartupService} from '../../../service-ui/startup.service';
import {ServerErrorToastService} from '../../../service-ui/server-error-toast.service';
import {ProjectMappingService} from '../../../service-data/project-mapping.service';
import {OrganizationService} from '../../../service-data/organization.service';
import {ServiceProviderService} from '../../../service-data/service-provider.service';
import {LanguagePipe} from '../../../pipe/language.pipe';
import {technicalErrorDetail} from '../../../service-data/http-error';
import {isEndpointNotBuilt} from '../mapping-write-unavailable';
import {
  ProjectMappingDashboardRow,
  ProjectMappingOrganizationState,
  ProjectMappingVocabulary
} from '../../../../model/ProjectMapping';
import {hasPlan as hasPlanRow, hasReadyPlan as hasReadyPlanRow, mergeDashboardRows} from './mapping-row-state';

/**
 * The operator dashboard over organizations, for `network` and
 * `serviceProvider`.
 *
 * ## The whole screen exists to show one number
 *
 * `needsDecision` -- how many proposed groupings need an answer from that
 * customer. Zero means an operator finishes the organization in one click and
 * the customer is never involved. Non-zero means somebody picks up the phone.
 * The distribution is expected to be heavily skewed to zero, and the value of
 * the table is that it sorts the twenty organizations worth a conversation out
 * of the three hundred that are not. Everything else in the row -- unmapped
 * counts, band counts, run age -- is supporting detail, and is laid out as
 * such.
 *
 * Singletons deliberately never set `needsDecision`. That is the rule that
 * keeps the number meaningful: a badge that is non-zero for everyone is a
 * badge nobody reads.
 *
 * ## Selecting an organization opens the SAME screen the customer sees
 *
 * Not a similar one. `ProjectMappingReviewComponent` is rendered for all
 * three roles against one endpoint, so an operator on the phone is looking at
 * exactly what the customer is describing.
 *
 * ## Scope
 *
 * The server scopes rows itself, and for `serviceProvider` it deliberately
 * does NOT reuse `Organizations.all()`: that query requires a PrintNodeOrder
 * within three months, which for mapping is backwards -- a dormant catalogue
 * with no recent print activity is precisely what needs mapping. If an
 * organization you expect is missing from other serviceProvider screens, that
 * recency filter is why, and it is a server-side question.
 */
@Component({
  selector: 'app-project-mapping-dashboard',
  templateUrl: './project-mapping-dashboard.component.html',
  styleUrls: ['./project-mapping-dashboard.component.css']
})
export class ProjectMappingDashboardComponent implements OnInit, OnDestroy, AfterViewInit, LoggedInCallback, RoleChangeListener {

  /**
   * Recomputed by {@link updateColumns} rather than derived in a getter: a
   * getter returning a fresh array hands `matHeaderRowDef` a new identity on
   * every change-detection pass, which re-renders the header continuously.
   */
  columns: string[] = ProjectMappingDashboardComponent.BASE_COLUMNS.slice();

  private static readonly BASE_COLUMNS =
    ['name', 'state', 'needsDecision', 'proposed', 'unmapped', 'run', 'actions'];

  public dataSource = new MatTableDataSource<ProjectMappingDashboardRow>();
  loading = false;
  /** True when the last load failed. An error is never shown as "no work". */
  loadFailed = false;

  /** Short technical line. See `technicalErrorDetail`. May be empty. */
  loadErrorDetail = '';
  /** Hide organizations with nothing outstanding. On by default: see above. */
  onlyNeedingAttention = false;
  planningOrgId = 0;

  /**
   * A reload nobody asked for is in flight.
   *
   * Deliberately NOT `loading`: that one takes the table off the screen,
   * which is right when an operator pressed Refresh and wrong when a message
   * arrived from the server on its own schedule.
   */
  refreshing = false;

  /** The last background refresh failed, so the table may be out of date. */
  refreshFailed = false;

  /** Pending coalesced background refresh. See {@link scheduleQuietRefresh}. */
  private refreshTimer: any = null;

  /**
   * How long to wait before acting on a run message.
   *
   * A plan that finishes quickly, an apply started from the same row, and
   * another operator's run can all land within a second or two of each
   * other. Reloading once per message is several whole-table reloads for one
   * operator action; one reload a moment later is the same information.
   */
  private static readonly REFRESH_COALESCE_MS = 1200;

  /** Admin email by organization id, joined client-side and kept. */
  private adminEmailById: { [id: number]: string } = {};

  /**
   * Organization ids the email join has already answered for -- including
   * the ones it answered "no admin user" for. Without that distinction a
   * background refresh cannot tell "we have no email for this organization"
   * from "we have not asked yet", and so asks again every single time.
   */
  private adminEmailKnown: { [id: number]: boolean } = {};

  /**
   * Free text over the three things an operator actually knows about a
   * customer who has just telephoned: the organization name, its id, and the
   * email address of the person who signed up.
   *
   * That triple is deliberately identical to the one
   * `SelectRoleComponent.filterOrganizations` matches on, down to the
   * case-insensitive substring test. An operator moves between the role
   * picker and this screen constantly, and a search box that matched a
   * different set of fields in each is worse than no search box: it teaches
   * one habit and then punishes it.
   */
  search = '';

  /**
   * Which §4 states to show. **Empty means every state**, not none -- an
   * empty multi-select reads as "no filter applied", whereas defaulting to a
   * subset would silently hide organizations an operator believes they are
   * looking at, which is the same class of lie as rendering a failed load as
   * an empty table.
   */
  selectedStates: ProjectMappingOrganizationState[] = [];
  readonly allStates: ProjectMappingOrganizationState[] = ProjectMappingVocabulary.ORGANIZATION_STATES;

  /**
   * False until the admin-email join has landed. The search hint says so,
   * because "I searched for their email address and got nothing back" must
   * not be mistaken for "that customer is not in the list".
   */
  adminEmailsLoaded = false;

  /**
   * Narrow the table to one service provider. `0` means all of them.
   *
   * Single-select, not multi: the question this answers is "show me my
   * problem accounts for one SP", which is a conversation with one service
   * provider at a time. A multi-select here would offer a union nobody has
   * ever needed and make the trigger unreadable.
   */
  selectedServiceProviderId = 0;

  /**
   * The service providers actually represented in the loaded rows, named and
   * sorted -- NOT every service provider in the network.
   *
   * Offering an SP with no organizations in scope produces an empty table
   * that looks like a bug, and the operator has no way to tell that from a
   * filter that is working correctly.
   */
  serviceProviderOptions: { id: number, name: string }[] = [];

  /** Resolved `ServiceProvider.Name` by id. Empty until the lookup lands. */
  private serviceProviderNames: { [id: number]: string } = {};

  /**
   * The table lives behind `*ngIf="!loading && !loadFailed"`, which makes
   * both of these moving targets: every load destroys the table and the next
   * render builds a NEW MatSort and a NEW MatPaginator. A `static: true`
   * query does not see an element inside an embedded view at all, and even a
   * dynamic one assigned once in `ngAfterViewInit` leaves
   * `dataSource.sort` and `dataSource.paginator` pointing at the instances
   * from the previous render.
   *
   * That is why the table used to come back unsorted and unpaged after a
   * reload: the controls on screen were wired to objects nothing was
   * rendering any more, so the paginator stopped limiting anything and all
   * several hundred rows were laid out at once. Setters, therefore -- the
   * data source is re-wired each time the view hands over a new instance.
   */
  sort: MatSort;
  paginator: MatPaginator;

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if ( sort ) {
      this.sort = sort;
      this.dataSource.sort = sort;
    }
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    if ( paginator ) {
      this.paginator = paginator;
      this.dataSource.paginator = paginator;
    }
  }
  @ViewChild('mainSideNav', { static: true }) mainSideNav: MatSidenav;

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  private rows: ProjectMappingDashboardRow[] = [];
  private onDestroySubject: Subject<boolean> = new Subject();

  constructor(public router: Router,
              public startupService: StartupService,
              public projectMappingService: ProjectMappingService,
              public organizationService: OrganizationService,
              public serviceProviderService: ServiceProviderService,
              public serverErrorToastService: ServerErrorToastService,
              public changeDetectorRef: ChangeDetectorRef,
              public media: MediaMatcher) {
    this.startupService.addRoleChangeListener(this);
    this.updateColumns();
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit(): void {
    this.startupService.isAuthenticated(this);

    // Run-level only. An apply publishes roughly one BOOK/DRAFT message per
    // item -- thousands for a large organization -- and counting those to
    // keep a table fresh would be thousands of change detection passes to
    // move one number.
    this.projectMappingService.runUpdates.pipe(takeUntil(this.onDestroySubject)).subscribe(run => {
      if ( !run ) {
        return;
      }
      const row = this.rows.find(r => r.run && r.run.runId === run.runId);
      if ( row ) {
        // The phase lands on the row immediately, so the spinner and the
        // cursor stop being wrong the moment we know better. The counts need
        // the server and arrive with the refresh.
        row.run.phase = run.phase;
        if ( run.phase === 'PLANNED' || run.phase === 'APPLIED' || run.phase === 'REVERTED' || run.phase === 'FAILED' ) {
          this.scheduleQuietRefresh();
        }
      } else {
        // A run we have never seen -- another operator planned something, or
        // this session loaded before the run existed. Re-read rather than
        // guess at the row.
        this.scheduleQuietRefresh();
      }
    });
  }

  ngAfterViewInit(): void {
    // Sort and paginator are wired by their setters above, because they do
    // not survive a reload. This accessor belongs to the data source, which
    // does, so it is set once and never again.
    this.dataSource.sortingDataAccessor = (row: ProjectMappingDashboardRow, column: string) => {
      switch ( column ) {
        case 'name':          return (row.name || '').toLowerCase();
        case 'needsDecision': return row.proposals ? row.proposals.needsDecision : 0;
        case 'proposed':      return row.proposals ? row.proposals.confident + row.proposals.review : 0;
        case 'unmapped':      return row.unmappedBooks + row.unmappedDrafts;
        case 'run':           return row.run && row.run.planned ? new Date(row.run.planned).getTime() : 0;
        // Sort by the NAME an operator can read, falling back to the id only
        // where the name lookup has not resolved one.
        case 'serviceProvider':
          return (this.serviceProviderName(row.serviceProvider_ID) || String(row.serviceProvider_ID)).toLowerCase();
        default:              return row[column];
      }
    };
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    // A timer that fires after the component is gone reloads a screen nobody
    // is looking at, through a service whose subscription has just been torn
    // down.
    if ( this.refreshTimer ) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  /**
   * Fold a burst of run messages into a single background reload.
   *
   * First message wins the timer and later ones ride along, rather than each
   * one pushing the deadline out -- a run that chatters would otherwise
   * postpone the refresh indefinitely, which is the one outcome worse than
   * refreshing too often.
   */
  private scheduleQuietRefresh(): void {
    if ( this.refreshTimer || this.loading ) {
      return;
    }
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.load(true);
    }, ProjectMappingDashboardComponent.REFRESH_COALESCE_MS);
  }

  isLoggedIn(message: string, isLoggedIn: boolean): void {
    if ( !isLoggedIn ) {
      this.router.navigate(['/home/login']);
    } else {
      this.load();
    }
  }

  roleChanged(role: string, roleId: string): void {
    // A serviceProvider sees only its own organizations, so the column and
    // the filter both disappear -- and a stale selection left behind would
    // silently filter the new role's table by an id it cannot see.
    this.updateColumns();
    this.selectedServiceProviderId = 0;
    this.serviceProviderOptions = [];
    // A different role sees a different set of organizations, so the
    // client-side email join starts again from nothing rather than carrying
    // addresses across a scope boundary.
    this.adminEmailById = {};
    this.adminEmailKnown = {};
    this.adminEmailsLoaded = false;
    this.load();
  }

  /**
   * The service provider column and filter are for `network` only.
   *
   * A `serviceProvider` operator is already scoped to its own organizations
   * server-side (§2), so every row would carry the same value: a column of
   * one repeated name is pure noise, and a filter over a single option is a
   * control that cannot do anything.
   */
  get showServiceProvider(): boolean {
    return this.startupService.selectedRole === 'network';
  }

  private updateColumns(): void {
    const columns = ProjectMappingDashboardComponent.BASE_COLUMNS.slice();
    if ( this.showServiceProvider ) {
      // Immediately after the organization, which is what it qualifies.
      columns.splice(1, 0, 'serviceProvider');
    }
    this.columns = columns;
  }

  serviceProviderName(id: number): string {
    return this.serviceProviderNames[id] || '';
  }

  serviceProviderChanged(id: number): void {
    this.selectedServiceProviderId = id || 0;
    this.refilterFromPageOne();
  }

  /**
   * Resolve `serviceProvider_ID` into names.
   *
   * `getServiceProviders()` caches, so on a screen reached after the service
   * providers list this costs nothing. It also swallows its own errors and
   * answers `[]`, which is why nothing here treats an empty list as fatal:
   * the column falls back to the bare id, which still sorts and still filters.
   */
  private loadServiceProviderNames(): void {
    if ( !this.showServiceProvider ) {
      return;
    }
    this.serviceProviderService.getServiceProviders()
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(
        list => {
          (list || []).forEach(serviceProvider => {
            if ( serviceProvider && serviceProvider.ID ) {
              this.serviceProviderNames[serviceProvider.ID] = serviceProvider.Name || '';
            }
          });
          this.rebuildServiceProviderOptions();
        },
        error => {
          console.error('ProjectMappingDashboardComponent: service provider names unavailable', error);
          this.rebuildServiceProviderOptions();
        }
      );
  }

  private rebuildServiceProviderOptions(): void {
    if ( !this.showServiceProvider ) {
      this.serviceProviderOptions = [];
      return;
    }
    const seen: { [id: number]: boolean } = {};
    const options: { id: number, name: string }[] = [];
    this.rows.forEach(row => {
      const id = row.serviceProvider_ID;
      if ( !id || seen[id] ) {
        return;
      }
      seen[id] = true;
      options.push({ id: id, name: this.serviceProviderName(id) });
    });
    options.sort((a, b) => {
      const an = (a.name || String(a.id)).toLowerCase();
      const bn = (b.name || String(b.id)).toLowerCase();
      return an < bn ? -1 : ( an > bn ? 1 : 0 );
    });
    this.serviceProviderOptions = options;
    // A selection whose organizations have just gone out of scope would
    // filter the table down to nothing with the cause off screen.
    if ( this.selectedServiceProviderId > 0 && !seen[this.selectedServiceProviderId] ) {
      this.selectedServiceProviderId = 0;
      this.applyFilter();
    }
  }

  /**
   * Re-read the dashboard.
   *
   * `quiet` marks a reload nobody asked for -- the ones a run message
   * triggers a second or two after a click. The difference is not cosmetic:
   *
   *   - A loud load takes the table away: `loading` drives the `*ngIf`
   *     around it, so the table is destroyed and a spinner stands in its
   *     place. The page collapses to the height of that spinner, the scroll
   *     position collapses with it, and when the table comes back the row
   *     the operator was reading is somewhere else. That is right for
   *     Refresh and for a role change. It is wrong for a message.
   *   - A quiet load leaves the table mounted and folds the new values into
   *     the row objects already on screen (see {@link mergeRows}), so
   *     nothing is destroyed, the pager stays on its page, the sort stays
   *     put and the scroll position never moves.
   *   - A quiet load that FAILS does not blank the table. The rows on screen
   *     are still the rows we had a moment ago; it says the list may be out
   *     of date instead, which raises the question rather than closing it.
   */
  load(quiet: boolean = false): void {
    if ( !this.projectMappingService.isOperator() ) {
      // An `organization` has no dashboard: there is exactly one organization
      // it could look at, which is the review screen itself.
      this.router.navigate(['/secure/projectreview']);
      return;
    }
    if ( quiet ) {
      this.refreshing = true;
    } else {
      this.loading = true;
      this.loadFailed = false;
      this.loadErrorDetail = '';
    }
    this.refreshFailed = false;
    this.projectMappingService.getDashboard().subscribe(
      rows => {
        this.loading = false;
        this.refreshing = false;
        this.mergeRows(rows || []);
        this.rebuildServiceProviderOptions();
        this.applyFilter();
        // Both of these are second requests for things the dashboard payload
        // does not carry. On a background refresh they are asked for only
        // when an organization has turned up that neither has covered yet --
        // otherwise every run message would cost three requests instead of
        // one, to re-learn what we already know.
        if ( !quiet || this.hasUnknownAdminEmails() ) {
          this.loadAdminEmails();
        }
        if ( !quiet || this.hasUnresolvedServiceProviderNames() ) {
          this.loadServiceProviderNames();
        }
      },
      error => {
        if ( quiet ) {
          this.refreshing = false;
          this.refreshFailed = true;
          console.error('ProjectMappingDashboardComponent: background refresh failed', error);
          return;
        }
        this.loading = false;
        this.rows = [];
        this.dataSource.data = [];
        this.loadFailed = true;
        this.loadErrorDetail = technicalErrorDetail(error);
        console.error('ProjectMappingDashboardComponent: dashboard load failed', error);
      }
    );
  }

  /**
   * Fold a freshly read dashboard into the rows already on screen, keeping
   * the SAME row object for every organization that is still there.
   *
   * MatTable diffs its data by object identity. Handing it a new array of
   * new objects therefore destroys and re-creates every row view even when
   * not one value changed -- the table flashes, anything the browser was
   * holding onto inside those rows is gone, and on a long table it is a
   * visible redraw. Mutating in place means the differ sees no change at all
   * and the only thing that updates is the handful of bindings whose values
   * actually moved.
   *
   * Row order follows the server's, as it did before: new organizations
   * appear where the server puts them, and ones no longer in scope drop out
   * by simply not being copied across.
   */
  private mergeRows(incoming: ProjectMappingDashboardRow[]): void {
    this.rows = mergeDashboardRows(this.rows, incoming, this.adminEmailById);
  }

  /**
   * True when some organization on the table has never been covered by the
   * email join -- a new organization, typically.
   */
  private hasUnknownAdminEmails(): boolean {
    return this.rows.some(row => !this.adminEmailKnown[row.org_ID]);
  }

  /** True when some row's service provider still has no resolved name. */
  private hasUnresolvedServiceProviderNames(): boolean {
    if ( !this.showServiceProvider ) {
      return false;
    }
    return this.rows.some(row => !!row.serviceProvider_ID && !this.serviceProviderNames[row.serviceProvider_ID]);
  }

  applyFilter(): void {
    const query = (this.search || '').trim().toLowerCase();
    this.dataSource.data = this.rows.filter(row => {
      if ( this.onlyNeedingAttention && !(this.needsDecision(row) > 0 || this.needsOperator(row)) ) {
        return false;
      }
      if ( this.selectedStates.length > 0 && this.selectedStates.indexOf(row.state) < 0 ) {
        return false;
      }
      if ( this.showServiceProvider
           && this.selectedServiceProviderId > 0
           && row.serviceProvider_ID !== this.selectedServiceProviderId ) {
        return false;
      }
      return this.matches(row, query);
    });
  }

  /**
   * Same three fields and the same case-insensitive substring test as
   * `SelectRoleComponent.filterOrganizations`. Kept deliberately dumb: an
   * operator reading an id off a support ticket pastes it in whole, and a
   * cleverer matcher is one more thing that can decline to find a customer
   * who is demonstrably there.
   */
  private matches(row: ProjectMappingDashboardRow, query: string): boolean {
    if ( !query ) { return true; }
    if ( row.name && row.name.toLowerCase().indexOf(query) >= 0 ) { return true; }
    if ( String(row.org_ID).indexOf(query) >= 0 ) { return true; }
    if ( row.adminEmail && row.adminEmail.toLowerCase().indexOf(query) >= 0 ) { return true; }
    return false;
  }

  /**
   * Re-filter after an operator changed a control, and go back to page one.
   *
   * Not folded into `applyFilter` because that also runs when fresh data
   * arrives, and resetting the pager underneath somebody reading page four of
   * an unchanged filter is its own small betrayal.
   */
  private refilterFromPageOne(): void {
    this.applyFilter();
    if ( this.paginator ) {
      this.paginator.firstPage();
    }
  }

  searchChanged(value: string): void {
    this.search = value || '';
    this.refilterFromPageOne();
  }

  clearSearch(): void {
    this.search = '';
    this.refilterFromPageOne();
  }

  statesChanged(states: ProjectMappingOrganizationState[]): void {
    this.selectedStates = states || [];
    this.refilterFromPageOne();
  }

  clearStates(): void {
    this.selectedStates = [];
    this.refilterFromPageOne();
  }

  /**
   * A search or status filter is on, as opposed to the "needs attention"
   * toggle. The empty-table message turns on this distinction: "nothing
   * matched what you typed" and "nothing needs doing" are opposite news.
   */
  get isSearchOrStatusFiltered(): boolean {
    return this.selectedStates.length > 0
      || (this.search || '').trim().length > 0
      || (this.showServiceProvider && this.selectedServiceProviderId > 0);
  }

  clearAllFilters(): void {
    this.search = '';
    this.selectedStates = [];
    this.selectedServiceProviderId = 0;
    this.onlyNeedingAttention = false;
    this.refilterFromPageOne();
  }

  /** True when the table is showing less than everything the server sent. */
  get isFiltered(): boolean {
    return this.onlyNeedingAttention || this.isSearchOrStatusFiltered;
  }

  get shownOrganizations(): number {
    return this.dataSource.data.length;
  }

  toggleOnlyNeedingAttention(): void {
    this.onlyNeedingAttention = !this.onlyNeedingAttention;
    this.refilterFromPageOne();
  }

  /**
   * Join the primary user's email address onto the rows.
   *
   * It does not arrive on the dashboard payload: §3.1 carries `name`,
   * `org_ID` and `serviceProvider_ID` and nothing else a human recognises.
   * So it comes from `organization/activeForRole` -- the same endpoint, and
   * the same rows, the role picker already searches.
   *
   * Deliberately fired AFTER the table has rendered and deliberately
   * best-effort. An operator who already knows the organization name must not
   * wait on a second request to use the screen, and a failure here costs one
   * search field rather than the dashboard.
   */
  private loadAdminEmails(): void {
    const role = this.startupService.selectedRole;
    const roleId = this.startupService.selectedRoleId;
    if ( !role || !roleId ) {
      return;
    }
    this.organizationService.getActiveOrganizationsForRole(role, roleId)
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(
        list => {
          // Cached rather than merely applied. The dashboard payload carries
          // no email, so every background refresh would otherwise have to
          // re-request this list to avoid blanking the column.
          (list || []).forEach(org => {
            if ( !org || !org.ID ) {
              return;
            }
            this.adminEmailKnown[org.ID] = true;
            if ( org.adminEmail ) {
              this.adminEmailById[org.ID] = org.adminEmail;
            }
          });
          this.rows.forEach(row => {
            if ( this.adminEmailById[row.org_ID] ) {
              row.adminEmail = this.adminEmailById[row.org_ID];
            }
          });
          this.adminEmailsLoaded = true;
          this.applyFilter();
        },
        error => {
          // NOT a toast: nothing the operator asked for has failed, and name
          // and id search still work. The hint under the box says which field
          // is missing, so an empty result is not read as an absent customer.
          this.adminEmailsLoaded = false;
          console.error('ProjectMappingDashboardComponent: admin emails unavailable', error);
        }
      );
  }

  needsDecision(row: ProjectMappingDashboardRow): number {
    return row.proposals ? row.proposals.needsDecision : 0;
  }

  proposedCount(row: ProjectMappingDashboardRow): number {
    return row.proposals ? row.proposals.confident + row.proposals.review : 0;
  }

  /**
   * May "Create the clear ones" be offered on this row?
   *
   * ---------------------------------------------------------------------------
   * WHY THIS IS NOT JUST `proposedCount(row) > 0` (reported 2026-10-28)
   * ---------------------------------------------------------------------------
   * An operator saw a row reading **Complete**, with nothing unmapped, still
   * offering this button beside a stale "1 needs a decision".
   *
   * The row mixes two ages of data and the button was keyed off the wrong one.
   * `state` is recomputed on every read from the live unmapped counts --
   * "COMPLETE wins over everything" (contract §3.1) -- whereas every value
   * under `proposals` is a **stored counter written once at plan time**. An
   * apply updates only `countApplied`, `countSuperseded`, `countFailed` and
   * `countDecidedAwaitingApply`; `countConfident`, `countReview` and
   * `countNeedsDecision` keep their planning values forever. So
   * `proposedCount` never decays, and the button was offered indefinitely on
   * organizations with nothing left to build.
   *
   * Pressing it there is not destructive -- apply revalidates every proposal
   * against live `work_ID`s and counts an already-mapped one as superseded --
   * but it is an action that cannot do anything, reported as though it had.
   *
   * Gated on the UNMAPPED COUNT rather than on `state === 'COMPLETE'`
   * deliberately: that is the fact COMPLETE is derived from, it is live, and it
   * stays correct if the state vocabulary ever changes.
   *
   * Known limitation, left alone rather than guessed at: a row can have
   * unmapped items where every remaining proposal needs a decision, so
   * `NO_DECISION_NEEDED` would still build nothing. The row carries no count of
   * "proposals needing no decision" to test, and inferring one by subtracting
   * `needsDecision` would be arithmetic on two numbers that count different
   * things. That case shows the button and reports honestly that nothing was
   * created.
   */
  canApplySimple(row: ProjectMappingDashboardRow): boolean {
    return !!row.run && this.proposedCount(row) > 0 && this.unmappedCount(row) > 0;
  }

  /**
   * True while a run is mid-flight, for either phase.
   *
   * Named apart from {@link isPlanning} because it gates the APPLY button, and
   * a template reading `isPlanning` there invites the next reader to "fix" it.
   * Both phases must block: the server claims the run before enqueueing, so a
   * second apply while one is in flight is refused with
   * `409 PROJECT_MAPPING_APPLY_IN_PROGRESS` -- which this screen would report
   * as "The mapping could not be applied. Nothing has been created.", false
   * twice over while an apply is running.
   */
  isBusy(row: ProjectMappingDashboardRow): boolean {
    return this.isPlanning(row);
  }

  unmappedCount(row: ProjectMappingDashboardRow): number {
    return row.unmappedBooks + row.unmappedDrafts;
  }

  /** True when the next move belongs to an operator rather than the customer. */
  needsOperator(row: ProjectMappingDashboardRow): boolean {
    return ProjectMappingVocabulary.ORGANIZATION_STATES_NEEDING_OPERATOR.indexOf(row.state) >= 0;
  }

  /** "Simple" -- an operator can finish this one without contacting anybody. */
  isSimple(row: ProjectMappingDashboardRow): boolean {
    return this.needsDecision(row) === 0;
  }

  get totalNeedingDecision(): number {
    return this.rows.filter(row => this.needsDecision(row) > 0).length;
  }

  get totalOrganizations(): number {
    return this.rows.length;
  }

  /**
   * True when there is a plan to look at.
   *
   * A `NOT_PLANNED` organization carries no run at all -- `run` is null by
   * contract -- and `getReview` answers an error for it. Navigating there
   * therefore rendered the review screen's red "could not be loaded" panel,
   * which tells an operator the screen is broken when the truth is only that
   * nobody has worked anything out for that customer yet. That is the same
   * class of wrong answer the review screen's own comment warns about: it
   * closes the question instead of raising it.
   */
  hasPlan(row: ProjectMappingDashboardRow): boolean {
    return hasPlanRow(row);
  }

  /** True when the review screen will have proposals to show for this row. */
  hasReadyPlan(row: ProjectMappingDashboardRow): boolean {
    return hasReadyPlanRow(row);
  }

  /**
   * What selecting a row does: it opens the organization. Always.
   *
   * This is the screen's one basic function and it is now unconditional. The
   * previous version refused the click for organizations with no plan and
   * started a plan instead, which was meant to spare an operator a useless
   * error. It did the opposite: the dashboard payload reports `run: null`
   * and state `NOT_PLANNED` for EVERY organization -- the server builds that
   * row from live Book and Draft counts and does not consult the run table --
   * so "has a plan" was false everywhere and not one row would open.
   *
   * The error it was avoiding is gone anyway: the review screen now reads the
   * server's own reason code and says "this organization has not been planned
   * yet" with a Plan button, instead of reporting a failure.
   */
  selectRow(row: ProjectMappingDashboardRow): void {
    this.openOrganization(row);
  }

  /**
   * The row's own account of what clicking it will do.
   *
   * Localized in TS rather than with `i18n-title`, because one element cannot
   * carry alternative titles and the distinction is worth making.
   */
  rowTitle(row: ProjectMappingDashboardRow): string {
    if ( this.hasReadyPlan(row) ) {
      return LanguagePipe.get('Open this organization to review its proposed groupings.');
    }
    return LanguagePipe.get('Open this organization. It has not been planned yet, and the page will offer to plan it.');
  }

  /**
   * Row classes: the section 4 state, and whether a customer decision is
   * outstanding.
   *
   * Returned as one joined string rather than an array because `NgClass`
   * re-applies whenever the bound value changes identity -- a fresh array
   * every change-detection pass is a fresh identity every pass, for a value
   * that almost never actually changes.
   */
  rowClasses(row: ProjectMappingDashboardRow): string {
    const classes = ['row-state-' + row.state, 'openableRow'];
    if ( this.needsDecision(row) > 0 ) {
      classes.push('needsDecisionRow');
    }
    return classes.join(' ');
  }

  /**
   * How loud the Plan button is, by state.
   *
   * An organization nobody has planned needs that one button pressed before
   * the screen can do anything at all for it, so it is the filled one. A
   * stale plan is the amber "do it again". Everywhere else a re-plan is a
   * background option and must not compete with the apply button beside it.
   */
  planButtonClass(row: ProjectMappingDashboardRow): string {
    if ( this.isPlanning(row) ) {
      return 'planWorking';
    }
    if ( row.state === 'NOT_PLANNED' ) {
      return 'planNeeded';
    }
    if ( row.state === 'PLAN_STALE' ) {
      return 'planStale';
    }
    return 'planQuiet';
  }

  openOrganization(row: ProjectMappingDashboardRow): void {
    this.router.navigate(['/secure/projectmapping', row.org_ID]);
  }

  /**
   * `event` is optional: the actions-column button passes one and must stop
   * it reaching the row click behind it, while `selectRow` has none to pass.
   */
  plan(row: ProjectMappingDashboardRow, event?: Event): void {
    if ( event ) {
      event.stopPropagation();
    }
    this.planningOrgId = row.org_ID;
    this.projectMappingService.plan(row.org_ID).subscribe(
      run => {
        this.planningOrgId = 0;
        if ( run && run.runId ) {
          row.run = run;
        }
        // The plan is enqueued, not finished; the PROJECT_MAPPING_RUN message
        // for the PLANNED phase triggers the reload that fills in the counts.
      },
      error => {
        this.planningOrgId = 0;
        // reportLocalizedError, not reportError: this sentence must be in the
        // operator's language. The server's English goes to the console.
        this.serverErrorToastService.reportLocalizedError('projectMapping.plan', error,
          LanguagePipe.get('The mapping plan could not be started. Nothing has been changed.'));
      }
    );
  }

  /**
   * Apply everything that needs no decision. This is the one-click finish for
   * the majority of organizations, and the reason the `needsDecision` column
   * is worth looking at before anything else.
   */
  applySimple(row: ProjectMappingDashboardRow, event: Event): void {
    event.stopPropagation();
    // Re-checked here, not just in the template: a disabled attribute is a
    // presentation detail, and this is the call that reaches the server.
    if ( !row.run || !row.run.runId || !this.canApplySimple(row) || this.isBusy(row) ) {
      return;
    }
    this.projectMappingService.apply(row.run.runId, { selector: 'NO_DECISION_NEEDED' }).subscribe(
      () => { /* progress arrives on PROJECT_MAPPING_RUN */ },
      error => this.serverErrorToastService.reportLocalizedError('projectMapping.applySimple', error,
        isEndpointNotBuilt(error)
          ? LanguagePipe.get('This is not available yet. Nothing has been changed.')
          : LanguagePipe.get('The mapping could not be applied. Nothing has been created.'))
    );
  }

  isPlanning(row: ProjectMappingDashboardRow): boolean {
    return this.planningOrgId === row.org_ID
      || (!!row.run && (row.run.phase === 'PLANNING' || row.run.phase === 'APPLYING'));
  }
}
